import { z } from 'zod';
import { percentualSchema } from './comum.ts';
import { apenasDigitos } from './documento.ts';

/**
 * Peças fiscais compartilhadas pela emissão de nota de serviço (`.spec/015`,
 * decisões 88 a 90).
 *
 * Ficam aqui, e não no contrato de quem as usou primeiro, porque são lidas por
 * quatro cadastros — empresa, insumo, operação fiscal e a própria nota. A
 * segunda cópia de "código de seis dígitos, ou vazio" é a que diverge no
 * primeiro ajuste de mensagem.
 */

/**
 * Código fiscal de tamanho fixo, só dígitos — ou vazio.
 *
 * Aceita a pontuação e a descarta: o CNAE chega como `4120-4/00` do cartão do
 * CNPJ, e o código de serviço como `07.02.01` da lista da LC 116. Recusar o
 * ponto obrigaria a redigitar o que foi colado. O que se grava é o que a nota
 * leva, sem máscara.
 */
export function codigoFiscalOpcional(digitos: number, nome: string) {
  return z
    .string()
    .transform(apenasDigitos)
    .refine(
      (valor) => valor.length === 0 || valor.length === digitos,
      `${nome} tem ${digitos} dígitos.`,
    )
    .transform((valor) => (valor.length === 0 ? null : valor))
    .nullable()
    .prefault(null);
}

/** Alíquota opcional, de 0 a 100. Vazia vira `null`, que é "não informada". */
export const aliquotaOpcionalSchema = z
  .union([percentualSchema, z.literal(''), z.null()])
  .transform((valor) => (valor === '' || valor === null ? null : valor))
  .prefault(null)
  .refine((valor) => valor === null || Number(valor) <= 100, 'A alíquota não passa de 100%.');

/* -------------------------------------------------------------------------- */
/* Códigos do serviço                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Código de tributação nacional (`cTribNac`) — seis dígitos: item, subitem e
 * desdobro da lista da LC 116/2003. `070201` é "execução de obra por
 * empreitada".
 */
export const DIGITOS_CODIGO_TRIBUTACAO_NACIONAL = 6;
/** Nomenclatura Brasileira de Serviços — nove dígitos. */
export const DIGITOS_NBS = 9;
/** Código de tributação municipal — o desdobro que o município faz do nacional. */
export const DIGITOS_CODIGO_TRIBUTACAO_MUNICIPAL = 3;

export const codigoTributacaoNacionalSchema = codigoFiscalOpcional(
  DIGITOS_CODIGO_TRIBUTACAO_NACIONAL,
  'O código de tributação nacional',
);
export const nbsSchema = codigoFiscalOpcional(DIGITOS_NBS, 'A NBS');
export const codigoTributacaoMunicipalSchema = codigoFiscalOpcional(
  DIGITOS_CODIGO_TRIBUTACAO_MUNICIPAL,
  'O código de tributação municipal',
);

/** `070201` → `07.02.01`. Só exibição. */
export function formatarCodigoTributacaoNacional(codigo: string | null | undefined): string {
  if (!codigo || codigo.length !== DIGITOS_CODIGO_TRIBUTACAO_NACIONAL) return codigo ?? '';

  return `${codigo.slice(0, 2)}.${codigo.slice(2, 4)}.${codigo.slice(4)}`;
}

/* -------------------------------------------------------------------------- */
/* Tributação do ISS                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Como o ISS incide na operação — o `tribISSQN` da NFS-e nacional.
 *
 * Mora na **operação fiscal**, e a nota copia: é a natureza da operação, e não
 * do serviço — o mesmo serviço de engenharia é tributável vendido aqui e
 * exportação vendido a um tomador do exterior.
 */
export const TRIBUTACOES_ISS = ['TRIBUTAVEL', 'IMUNIDADE', 'EXPORTACAO', 'NAO_INCIDENCIA'] as const;
export const tributacaoIssSchema = z.enum(TRIBUTACOES_ISS);
export type TributacaoIss = z.infer<typeof tributacaoIssSchema>;

export const ROTULO_TRIBUTACAO_ISS: Readonly<Record<TributacaoIss, string>> = {
  TRIBUTAVEL: 'Tributável',
  IMUNIDADE: 'Imunidade',
  EXPORTACAO: 'Exportação de serviço',
  NAO_INCIDENCIA: 'Não incidência',
};

/**
 * As retenções que uma nota de serviço pode sofrer na fonte.
 *
 * A lista é a ordem em que a tela e os relatórios as mostram. `ISS` entra aqui
 * e não só em `tributacaoIss` porque são duas perguntas: *o ISS incide?* e
 * *quem o recolhe?* — o ISS retido incide, e quem paga a guia é o tomador.
 */
export const RETENCOES_DA_NOTA = ['ISS', 'IRRF', 'INSS', 'CSLL', 'PIS', 'COFINS'] as const;
export type RetencaoDaNota = (typeof RETENCOES_DA_NOTA)[number];

export const ROTULO_RETENCAO_DA_NOTA: Readonly<Record<RetencaoDaNota, string>> = {
  ISS: 'ISS',
  IRRF: 'IRRF',
  INSS: 'INSS',
  CSLL: 'CSLL',
  PIS: 'PIS',
  COFINS: 'COFINS',
};
